/**
 * Shared controlled values for public submission forms.
 * Keep option arrays here — do not duplicate across form components.
 */

import { formHref } from "@/constants/formAnchors";

export const CONTRACT_VERSION = "1";

export const REFERRAL_SOURCES = [
  "Instagram",
  "Facebook",
  "LinkedIn",
  "Google",
  "Film Festival",
  "Referral",
  "IMDb",
  "Website",
  "Word of Mouth",
  "Other",
];

export const US_STATES = [
  "AL", "AK", "AZ", "AR", "CA", "CO", "CT", "DE", "FL", "GA",
  "HI", "ID", "IL", "IN", "IA", "KS", "KY", "LA", "ME", "MD",
  "MA", "MI", "MN", "MS", "MO", "MT", "NE", "NV", "NH", "NJ",
  "NM", "NY", "NC", "ND", "OH", "OK", "OR", "PA", "RI", "SC",
  "SD", "TN", "TX", "UT", "VT", "VA", "WA", "WV", "WI", "WY",
  "DC",
];

export const YES_NO = ["Yes", "No"];

export const UNION_STATUS_OPTIONS = [
  "SAG-AFTRA",
  "SAG Eligible",
  "Non-Union",
  "Other",
];

/** Legacy casting values still accepted by the API. */
export const LEGACY_UNION_STATUS = ["SAG-Eligible"];

/** Actress retained as a legacy API value; not shown in the public picker. */
export const LEGACY_TALENT_TYPES = ["Actress"];

export const TALENT_TYPE_OPTIONS = [
  "Actor",
  "Voice Actor",
  "Host",
  "Model",
  "Stunt Performer",
  "Background Talent",
  "Child Actor",
  "Other",
];

export const ROLE_TYPE_OPTIONS = [
  "Lead",
  "Supporting",
  "Principal",
  "Day Player",
  "Featured",
  "Background",
];

/** Legacy role values still accepted by the API / historical records. */
export const LEGACY_ROLE_TYPES = ["Voice Over", "Commercial", "Stage"];

export const SPECIAL_SKILL_OPTIONS = [
  "Firearms Training",
  "Military Experience",
  "Law Enforcement",
  "Medical Experience",
  "Horseback Riding",
  "Motorcycle Riding",
  "Swimming",
  "Scuba Diving",
  "Dance",
  "Martial Arts",
  "Boxing",
  "Gymnastics",
  "Cheerleading",
  "Singing",
  "Musician",
  "Comedy / Improv",
  "Teleprompter",
  "Dialects / Accents",
  "Driving Stick Shift",
  "Other",
];

/**
 * Canonical Crew & Production / crew roles.
 * Shared by Crew & Production Submission and Talent→Crew crossover.
 */
export const CREW_POSITION_OPTIONS = [
  "Director",
  "Producer",
  "Executive Producer",
  "Line Producer",
  "Production Manager",
  "Production Coordinator",
  "Assistant Director",
  "Production Assistant",
  "Script Supervisor",
  "Director of Photography",
  "Camera Operator",
  "1st AC",
  "2nd AC",
  "Drone Operator",
  "Gaffer",
  "Key Grip",
  "Grip",
  "Sound Mixer",
  "Boom Operator",
  "Hair Stylist",
  "Makeup Artist",
  "Wardrobe",
  "Production Designer",
  "Art Director",
  "Set Decorator",
  "Props",
  "Location Manager",
  "Editor",
  "Colorist",
  "Composer",
  "VFX Artist",
  "BTS Photographer",
  "BTS Videographer",
  "Craft Services",
  "Transportation",
  "Security",
  "Equipment Rental",
  "Catering",
  "Vendor",
  "Production Company",
  "Other",
];

/** Grouped crew roles for searchable / collapsible mobile multi-select. */
export const CREW_POSITION_GROUPS = [
  {
    category: "Directing & Producing",
    options: [
      "Director",
      "Producer",
      "Executive Producer",
      "Line Producer",
      "Production Manager",
      "Production Coordinator",
      "Assistant Director",
      "Production Assistant",
      "Script Supervisor",
    ],
  },
  {
    category: "Camera & Lighting",
    options: [
      "Director of Photography",
      "Camera Operator",
      "1st AC",
      "2nd AC",
      "Drone Operator",
      "Gaffer",
      "Key Grip",
      "Grip",
    ],
  },
  {
    category: "Sound",
    options: ["Sound Mixer", "Boom Operator"],
  },
  {
    category: "Hair, Makeup & Wardrobe",
    options: ["Hair Stylist", "Makeup Artist", "Wardrobe"],
  },
  {
    category: "Art & Locations",
    options: [
      "Production Designer",
      "Art Director",
      "Set Decorator",
      "Props",
      "Location Manager",
    ],
  },
  {
    category: "Post-Production",
    options: ["Editor", "Colorist", "Composer", "VFX Artist"],
  },
  {
    category: "BTS",
    options: ["BTS Photographer", "BTS Videographer"],
  },
  {
    category: "Vendors & Production Support",
    options: [
      "Craft Services",
      "Transportation",
      "Security",
      "Equipment Rental",
      "Catering",
      "Vendor",
      "Production Company",
      "Other",
    ],
  },
];

export const PROJECT_TYPE_OPTIONS = [
  "Feature Film",
  "Short Film",
  "Television",
  "Limited Series",
  "Documentary",
  "Stage Production",
  "Digital Series",
  "Commercial",
  "Music Video",
  "Other",
];

export const GENRE_OPTIONS = [
  "Drama",
  "Comedy",
  "Thriller",
  "Horror",
  "Action",
  "Romance",
  "Documentary",
  "Faith-Based",
  "Family",
  "Sci-Fi",
  "Crime",
  "Mystery",
  "Other",
];

export const CURRENT_STAGE_OPTIONS = [
  "Idea",
  "Treatment",
  "First Draft",
  "Revised Draft",
  "Final Script",
  "In Development",
  "Pre-Production",
  "Production",
  "Post-Production",
  "Seeking Distribution",
];

/**
 * Partner types — capital, financing, sponsorship, distribution, strategy.
 * Operational crew / vendor roles belong in Crew & Production Submission, not here.
 */
export const PARTNER_TYPE_GROUPS = [
  {
    category: "Funding & Financing",
    interestType: "Funding Partner",
    options: ["Investor", "Executive Producer", "Financing Partner"],
  },
  {
    category: "Brand & Distribution",
    interestType: "Business / Strategic Partner",
    options: [
      "Brand Sponsor",
      "Product Placement",
      "Marketing Partner",
      "Distribution",
    ],
  },
  {
    category: "Strategic & Business",
    interestType: "Business / Strategic Partner",
    options: ["Strategic Partner", "Production Company", "Business Partner"],
  },
];

export const ALL_PARTNER_TYPES = PARTNER_TYPE_GROUPS.flatMap((g) => g.options);

export const FILE_RULES = {
  MAX_BYTES: 10 * 1024 * 1024,
  MAX_MB: 10,
  TALENT: {
    headshot: {
      maxFiles: 1,
      accept: ["application/pdf", "image/jpeg", "image/jpg", "image/png"],
      acceptAttr: ".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png",
      label: "JPG, PNG, or PDF",
      hint: "JPG, PNG, or PDF. Max 10 MB.",
    },
    resume: {
      maxFiles: 1,
      accept: ["application/pdf", "image/jpeg", "image/jpg", "image/png"],
      acceptAttr: ".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png",
      label: "PDF, JPG, or PNG",
      hint: "Accepted file types: PDF, JPG, or PNG. Max 10 MB.",
    },
    additional_photos: {
      maxFiles: 4,
      accept: ["image/jpeg", "image/jpg", "image/png"],
      acceptAttr: ".jpg,.jpeg,.png,image/jpeg,image/png",
      label: "JPG or PNG",
      hint: "Optional: Upload up to 4 additional current photos. JPG or PNG. Max 10 MB each.",
    },
  },
  CREW: {
    resume: {
      maxFiles: 1,
      accept: ["application/pdf", "image/jpeg", "image/jpg", "image/png"],
      acceptAttr: ".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png",
      label: "PDF, JPG, or PNG",
      hint: "Accepted file types: PDF, JPG, or PNG. Max 10 MB.",
    },
    headshot: {
      maxFiles: 1,
      accept: ["application/pdf", "image/jpeg", "image/jpg", "image/png"],
      acceptAttr: ".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png",
      label: "PDF, JPG, or PNG",
      hint: "Accepted file types: PDF, JPG, or PNG. Max 10 MB.",
    },
  },
  PROJECT: {
    pitch_deck: {
      maxFiles: 1,
      accept: ["application/pdf"],
      acceptAttr: ".pdf,application/pdf",
      label: "PDF",
      hint: "This form cannot upload files. Paste a shareable link to your pitch deck in the message.",
    },
    script: {
      maxFiles: 1,
      accept: ["application/pdf"],
      acceptAttr: ".pdf,application/pdf",
      label: "PDF",
      hint: "This form cannot upload files. Paste a shareable link to your script in the message.",
    },
  },
};

export const SUCCESS_MESSAGE = "Thank you. Your submission has been received.";
export const FAILURE_MESSAGE =
  "Something went wrong. Please try again or contact SoKno Productions.";

export const CONSENT_TEXT =
  "I authorize SoKno Productions to review the information I submit, retain it for business or production consideration, and contact me about relevant opportunities or inquiries. This does not guarantee employment, casting, project review, investment, a response, or any transfer of script or intellectual-property ownership.";

/** Talent-specific authorization copy (public casting form). */
export const TALENT_CONSENT_TEXT =
  "I authorize SoKno Productions to review and retain my submitted information and contact me regarding relevant opportunities. Submission does not guarantee employment, casting, project consideration, investment, or a response and does not transfer ownership of any submitted intellectual property.";

/**
 * Deterministic subject helper for notification contracts.
 * Displays up to the first three values, then "+N more".
 */
export function formatMultiSelectSubject(values, max = 3) {
  const list = (values || []).filter(Boolean);
  if (list.length === 0) return "";
  if (list.length <= max) return list.join(", ");
  const shown = list.slice(0, max).join(", ");
  return `${shown} +${list.length - max} more`;
}

export function partnerInterestTypeForSelections(partnerTypes) {
  for (const group of PARTNER_TYPE_GROUPS) {
    if (partnerTypes.some((t) => group.options.includes(t))) {
      return group.interestType;
    }
  }
  return null;
}

/** Submission hub card definitions — single source for home + /submit. */
export const SUBMISSION_HUB_CARDS = [
  {
    id: "talent",
    category: "Talent & Casting",
    title: "Talent Submission",
    audience:
      "Actors, voice actors, models, background talent, child actors, stunt performers, and performers.",
    cta: "Submit Talent",
    to: formHref("talent"),
  },
  {
    id: "production-network",
    category: "Crew & Production",
    title: "Crew & Production Submission",
    audience:
      "Crew, vendors, makeup, hair, wardrobe, lighting, grip, sound, ADs, PAs, locations, catering, transportation, security, BTS, equipment rental, and production support.",
    cta: "Submit Crew & Production",
    to: formHref("crew"),
  },
  {
    id: "project",
    category: "Project Development",
    title: "Project Inquiry",
    audience:
      "Scripts, film projects, development conversations, producing opportunities, and collaboration.",
    cta: "Submit Project Inquiry",
    to: formHref("project"),
  },
  {
    id: "partner",
    category: "Partnerships",
    title: "Partner With SoKno",
    audience:
      "Investors, sponsors, financing partners, product placement, distribution, brands, production companies, and strategic partners.",
    cta: "Submit Partnership Interest",
    to: formHref("partner"),
  },
  {
    id: "contact",
    category: "General",
    title: "General Inquiry",
    audience: "Questions that do not fit another category.",
    cta: "Send Message",
    to: formHref("contact"),
  },
];

/**
 * Short navigation labels for the Connect With SoKno quick-access strip.
 * These are nav labels only — form titles/API keys stay on SUBMISSION_HUB_CARDS.
 */
export const SUBMISSION_QUICK_LINKS = [
  { id: "talent", label: "Talent Submission", to: formHref("talent") },
  { id: "crew", label: "Crew & Production", to: formHref("crew") },
  { id: "project", label: "Project Inquiry", to: formHref("project") },
  { id: "partner", label: "Partnerships", to: formHref("partner") },
  { id: "contact", label: "General Inquiry", to: formHref("contact") },
];
